import {
  buildDashboardItems,
  type DynamicGridItem,
  type DynamicGridItemMeta,
  type DynamicGridPosition,
} from '@cast/design-system';

import { WORKLOAD_CATEGORIES } from '../../_constants';
import type { WorkloadCategory } from '../../_constants';

export enum WorkloadsDashboardItemId {
  CpuUtilization = 'cpu-utilization',
  MemoryUtilization = 'memory-utilization',
  ResourceUtilization = 'resource-utilization',
  OomKills = 'oom-kills',
  WorkloadAutoscalerCoverage = 'workload-autoscaler-coverage',
  RecommendationStatus = 'recommendation-status',
  ScalingPolicies = 'scaling-policies',
  CpuImpact = 'cpu-impact',
  MemoryImpact = 'memory-impact',
  AutoscalerImpact = 'autoscaler-impact',
  ReclaimedResources = 'reclaimed-resources',
  SavingsSummary = 'savings-summary',
  NetworkCost = 'network-cost',
  NetworkTraffic = 'network-traffic',
  NetworkCostOverTime = 'network-cost-over-time',
  GpuWorkloads = 'gpu-workloads',
  GpuMemoryWaste = 'gpu-memory-waste',
  GpuIdleSpend = 'gpu-idle-spend',
  ReliabilityMap = 'reliability-map',
  WorkloadHealth = 'workload-health',
  WorkloadHealthTrafficShare = 'workload-health-traffic-share',
  P95LatencyDistribution = 'p95-latency-distribution',
  ComputeCost = 'compute-cost',
  BiggestCostMovers = 'biggest-cost-movers',
  ComputeSpend = 'compute-spend',
}

export type WorkloadsDashboardItem = DynamicGridItem & {
  id: WorkloadsDashboardItemId;
};

// Sizing convention:
// - Charts default to half-width (w: 6) and stay readable: minW: 6, minH: 4.
// - Summary cards (e.g. autoscaler impact) can shrink further: minW: 3, minH: 3.
const WORKLOADS_DASHBOARD_ITEM_META: Record<
  WorkloadsDashboardItemId,
  DynamicGridItemMeta & { id: WorkloadsDashboardItemId }
> = {
  [WorkloadsDashboardItemId.WorkloadAutoscalerCoverage]: {
    id: WorkloadsDashboardItemId.WorkloadAutoscalerCoverage,
    title: 'Workload Autoscaler coverage',
    description: 'Autoscaler coverage across workloads',
    w: 4,
    h: 4,
    minW: 4,
    minH: 4,
  },
  [WorkloadsDashboardItemId.RecommendationStatus]: {
    id: WorkloadsDashboardItemId.RecommendationStatus,
    title: 'Recommendation status',
    description: 'Status of applied recommendations',
    w: 4,
    h: 4,
    minW: 4,
    minH: 4,
  },
  [WorkloadsDashboardItemId.AutoscalerImpact]: {
    id: WorkloadsDashboardItemId.AutoscalerImpact,
    title: 'Workload Autoscaler impact',
    description: 'Summary of autoscaler adoption across workloads',
    w: 4,
    h: 3,
    minW: 3,
    minH: 3,
  },
  [WorkloadsDashboardItemId.ReclaimedResources]: {
    id: WorkloadsDashboardItemId.ReclaimedResources,
    title: 'Reclaimed resources',
    description: 'Average reclaimed CPU and memory resources',
    w: 4,
    h: 3,
    minW: 3,
    minH: 3,
  },
  [WorkloadsDashboardItemId.SavingsSummary]: {
    id: WorkloadsDashboardItemId.SavingsSummary,
    title: 'Workload Autoscaler savings',
    description: 'Available and realized monthly cost savings',
    w: 4,
    h: 3,
    minW: 3,
    minH: 3,
  },
  [WorkloadsDashboardItemId.ScalingPolicies]: {
    id: WorkloadsDashboardItemId.ScalingPolicies,
    title: 'Scaling policies',
    description: 'Distribution of scaling policies',
    w: 4,
    h: 3,
    minW: 3,
    minH: 3,
  },
  [WorkloadsDashboardItemId.CpuUtilization]: {
    id: WorkloadsDashboardItemId.CpuUtilization,
    title: 'CPU Utilization',
    description: 'CPU utilization over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadsDashboardItemId.MemoryUtilization]: {
    id: WorkloadsDashboardItemId.MemoryUtilization,
    title: 'Memory Utilization',
    description: 'Memory utilization over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadsDashboardItemId.ResourceUtilization]: {
    id: WorkloadsDashboardItemId.ResourceUtilization,
    title: 'Resource utilization',
    description: 'Average CPU and memory utilization',
    w: 4,
    h: 3,
    minW: 3,
    minH: 3,
  },
  [WorkloadsDashboardItemId.OomKills]: {
    id: WorkloadsDashboardItemId.OomKills,
    title: 'OOMKills',
    description: 'OOM kill events over time by workload',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadsDashboardItemId.CpuImpact]: {
    id: WorkloadsDashboardItemId.CpuImpact,
    title: 'CPU Impact',
    description: 'CPU autoscaling impact',
    w: 12,
    h: 5,
    minW: 6,
    minH: 4,
  },
  [WorkloadsDashboardItemId.MemoryImpact]: {
    id: WorkloadsDashboardItemId.MemoryImpact,
    title: 'Memory Impact',
    description: 'Memory autoscaling impact',
    w: 12,
    h: 5,
    minW: 6,
    minH: 4,
  },
  [WorkloadsDashboardItemId.NetworkCost]: {
    id: WorkloadsDashboardItemId.NetworkCost,
    title: 'Network cost',
    description: 'Total network cost for selected workloads',
    w: 6,
    h: 3,
    minW: 3,
    minH: 3,
  },
  [WorkloadsDashboardItemId.NetworkTraffic]: {
    id: WorkloadsDashboardItemId.NetworkTraffic,
    title: 'Network traffic',
    description: 'Total network traffic for selected workloads',
    w: 6,
    h: 3,
    minW: 3,
    minH: 3,
  },
  [WorkloadsDashboardItemId.NetworkCostOverTime]: {
    id: WorkloadsDashboardItemId.NetworkCostOverTime,
    title: 'Network cost',
    description: 'Top workloads by network cost over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadsDashboardItemId.GpuWorkloads]: {
    id: WorkloadsDashboardItemId.GpuWorkloads,
    title: 'GPU Workloads',
    description: 'Total GPU workloads and memory breakdown',
    w: 6,
    h: 3,
    minW: 3,
    minH: 3,
  },
  [WorkloadsDashboardItemId.GpuMemoryWaste]: {
    id: WorkloadsDashboardItemId.GpuMemoryWaste,
    title: 'Total GPU memory waste',
    description: 'Total GPU memory waste for selected workloads',
    w: 6,
    h: 3,
    minW: 3,
    minH: 3,
  },
  [WorkloadsDashboardItemId.GpuIdleSpend]: {
    id: WorkloadsDashboardItemId.GpuIdleSpend,
    title: 'Idle GPU spend',
    description: 'Top workloads by idle GPU spend over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 3,
  },
  [WorkloadsDashboardItemId.ReliabilityMap]: {
    id: WorkloadsDashboardItemId.ReliabilityMap,
    title: 'Reliability map',
    description: 'Workload reliability scatter chart with top offenders',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadsDashboardItemId.WorkloadHealth]: {
    id: WorkloadsDashboardItemId.WorkloadHealth,
    title: 'Workloads by health',
    description: 'Distribution of workload health status',
    w: 6,
    h: 2,
    minW: 3,
    minH: 2,
  },
  [WorkloadsDashboardItemId.WorkloadHealthTrafficShare]: {
    id: WorkloadsDashboardItemId.WorkloadHealthTrafficShare,
    title: 'Traffic distribution per workload health',
    description: 'Traffic share grouped by workload health',
    w: 6,
    h: 2,
    minW: 3,
    minH: 2,
  },
  [WorkloadsDashboardItemId.P95LatencyDistribution]: {
    id: WorkloadsDashboardItemId.P95LatencyDistribution,
    title: 'Workload P95 latency distribution',
    description: 'Distribution of workload P95 latency',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadsDashboardItemId.ComputeCost]: {
    id: WorkloadsDashboardItemId.ComputeCost,
    title: 'Compute cost',
    description: 'Current month compute spend and top spenders',
    w: 4,
    h: 3,
    minW: 3,
    minH: 3,
  },
  [WorkloadsDashboardItemId.BiggestCostMovers]: {
    id: WorkloadsDashboardItemId.BiggestCostMovers,
    title: 'Biggest cost movers',
    description: 'Workloads with the biggest cost changes',
    w: 4,
    h: 3,
    minW: 3,
    minH: 3,
  },
  [WorkloadsDashboardItemId.ComputeSpend]: {
    id: WorkloadsDashboardItemId.ComputeSpend,
    title: 'Compute spend',
    description: 'Compute spend over time with top workloads',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
};

const ALL_WORKLOADS_DASHBOARD_ITEM_IDS = Object.keys(
  WORKLOADS_DASHBOARD_ITEM_META
) as WorkloadsDashboardItemId[];

/**
 * Per-category default positions from Phase-1 Figma.
 * Intentional gaps reserve space for unimplemented cards (Attention needed,
 * Idle costs, etc.) — pair with DynamicGrid `compactor="none"`.
 */
const WORKLOADS_DASHBOARD_LAYOUTS: Record<
  WorkloadCategory,
  Partial<Record<WorkloadsDashboardItemId, DynamicGridPosition>>
> = {
  [WORKLOAD_CATEGORIES.DEFAULT]: {
    [WorkloadsDashboardItemId.AutoscalerImpact]: { x: 0, y: 0, w: 4, h: 3 },
    [WorkloadsDashboardItemId.RecommendationStatus]: {
      x: 0,
      y: 3,
      w: 4,
      h: 3,
    },
    [WorkloadsDashboardItemId.ComputeCost]: { x: 4, y: 0, w: 4, h: 4 },
    [WorkloadsDashboardItemId.WorkloadAutoscalerCoverage]: {
      x: 8,
      y: 0,
      w: 4,
      h: 4,
    },
    [WorkloadsDashboardItemId.ResourceUtilization]: {
      x: 4,
      y: 4,
      w: 4,
      h: 3,
    },
    [WorkloadsDashboardItemId.NetworkCost]: { x: 8, y: 4, w: 4, h: 3 },
    [WorkloadsDashboardItemId.OomKills]: { x: 0, y: 7, w: 12, h: 4 },
    [WorkloadsDashboardItemId.ReliabilityMap]: { x: 0, y: 11, w: 12, h: 4 },
    [WorkloadsDashboardItemId.CpuUtilization]: { x: 0, y: 15, w: 12, h: 4 },
    [WorkloadsDashboardItemId.MemoryUtilization]: { x: 0, y: 19, w: 12, h: 4 },
    [WorkloadsDashboardItemId.ComputeSpend]: { x: 0, y: 23, w: 12, h: 4 },
  },
  // Gap: Attention needed (0,0 w4 h4).
  [WORKLOAD_CATEGORIES.AUTOSCALING]: {
    [WorkloadsDashboardItemId.WorkloadAutoscalerCoverage]: {
      x: 4,
      y: 0,
      w: 4,
      h: 4,
    },
    [WorkloadsDashboardItemId.RecommendationStatus]: {
      x: 8,
      y: 0,
      w: 4,
      h: 4,
    },
    [WorkloadsDashboardItemId.ScalingPolicies]: { x: 0, y: 4, w: 4, h: 3 },
    [WorkloadsDashboardItemId.ReclaimedResources]: { x: 4, y: 4, w: 4, h: 3 },
    [WorkloadsDashboardItemId.SavingsSummary]: { x: 8, y: 4, w: 4, h: 3 },
    [WorkloadsDashboardItemId.CpuUtilization]: { x: 0, y: 7, w: 12, h: 4 },
    [WorkloadsDashboardItemId.MemoryUtilization]: { x: 0, y: 11, w: 12, h: 4 },
    [WorkloadsDashboardItemId.OomKills]: { x: 0, y: 15, w: 12, h: 4 },
  },
  [WORKLOAD_CATEGORIES.SAVINGS]: {
    [WorkloadsDashboardItemId.AutoscalerImpact]: { x: 0, y: 0, w: 4, h: 3 },
    [WorkloadsDashboardItemId.ReclaimedResources]: { x: 4, y: 0, w: 4, h: 3 },
    [WorkloadsDashboardItemId.SavingsSummary]: { x: 8, y: 0, w: 4, h: 3 },
    [WorkloadsDashboardItemId.CpuImpact]: { x: 0, y: 3, w: 12, h: 5 },
    [WorkloadsDashboardItemId.MemoryImpact]: { x: 0, y: 8, w: 12, h: 5 },
  },
  // Gap: Attention needed (0,0 w4 h3). Idle costs chart omitted below spend.
  [WORKLOAD_CATEGORIES.COMPUTE]: {
    [WorkloadsDashboardItemId.ComputeCost]: { x: 4, y: 0, w: 4, h: 3 },
    [WorkloadsDashboardItemId.BiggestCostMovers]: { x: 8, y: 0, w: 4, h: 3 },
    [WorkloadsDashboardItemId.ComputeSpend]: { x: 0, y: 3, w: 12, h: 4 },
  },
  [WORKLOAD_CATEGORIES.RELIABILITY]: {
    [WorkloadsDashboardItemId.ReliabilityMap]: { x: 0, y: 0, w: 12, h: 4 },
    [WorkloadsDashboardItemId.WorkloadHealth]: { x: 0, y: 4, w: 6, h: 2 },
    [WorkloadsDashboardItemId.WorkloadHealthTrafficShare]: {
      x: 6,
      y: 4,
      w: 6,
      h: 2,
    },
    [WorkloadsDashboardItemId.P95LatencyDistribution]: {
      x: 0,
      y: 6,
      w: 12,
      h: 4,
    },
  },
  [WORKLOAD_CATEGORIES.NETWORK]: {
    [WorkloadsDashboardItemId.NetworkCost]: { x: 0, y: 0, w: 6, h: 3 },
    [WorkloadsDashboardItemId.NetworkTraffic]: { x: 6, y: 0, w: 6, h: 3 },
    [WorkloadsDashboardItemId.NetworkCostOverTime]: {
      x: 0,
      y: 3,
      w: 12,
      h: 4,
    },
  },
  [WORKLOAD_CATEGORIES.GPU_UTILIZATION]: {
    [WorkloadsDashboardItemId.GpuWorkloads]: { x: 0, y: 0, w: 6, h: 3 },
    [WorkloadsDashboardItemId.GpuMemoryWaste]: { x: 6, y: 0, w: 6, h: 3 },
    [WorkloadsDashboardItemId.GpuIdleSpend]: { x: 0, y: 3, w: 12, h: 4 },
  },
};

const buildWorkloadsDashboardItems = (
  positions: Partial<Record<WorkloadsDashboardItemId, DynamicGridPosition>>
): WorkloadsDashboardItem[] =>
  buildDashboardItems(
    ALL_WORKLOADS_DASHBOARD_ITEM_IDS.map(
      (id) => WORKLOADS_DASHBOARD_ITEM_META[id]
    ),
    positions
  );

/** Per-category item geometry. Prefer this over the legacy mega-layout. */
export const WORKLOADS_DASHBOARD_ITEMS_BY_CATEGORY: Record<
  WorkloadCategory,
  WorkloadsDashboardItem[]
> = {
  [WORKLOAD_CATEGORIES.DEFAULT]: buildWorkloadsDashboardItems(
    WORKLOADS_DASHBOARD_LAYOUTS[WORKLOAD_CATEGORIES.DEFAULT]
  ),
  [WORKLOAD_CATEGORIES.AUTOSCALING]: buildWorkloadsDashboardItems(
    WORKLOADS_DASHBOARD_LAYOUTS[WORKLOAD_CATEGORIES.AUTOSCALING]
  ),
  [WORKLOAD_CATEGORIES.SAVINGS]: buildWorkloadsDashboardItems(
    WORKLOADS_DASHBOARD_LAYOUTS[WORKLOAD_CATEGORIES.SAVINGS]
  ),
  [WORKLOAD_CATEGORIES.COMPUTE]: buildWorkloadsDashboardItems(
    WORKLOADS_DASHBOARD_LAYOUTS[WORKLOAD_CATEGORIES.COMPUTE]
  ),
  [WORKLOAD_CATEGORIES.RELIABILITY]: buildWorkloadsDashboardItems(
    WORKLOADS_DASHBOARD_LAYOUTS[WORKLOAD_CATEGORIES.RELIABILITY]
  ),
  [WORKLOAD_CATEGORIES.NETWORK]: buildWorkloadsDashboardItems(
    WORKLOADS_DASHBOARD_LAYOUTS[WORKLOAD_CATEGORIES.NETWORK]
  ),
  [WORKLOAD_CATEGORIES.GPU_UTILIZATION]: buildWorkloadsDashboardItems(
    WORKLOADS_DASHBOARD_LAYOUTS[WORKLOAD_CATEGORIES.GPU_UTILIZATION]
  ),
};

const UTILIZATION_IDS: WorkloadsDashboardItemId[] = [
  WorkloadsDashboardItemId.CpuUtilization,
  WorkloadsDashboardItemId.MemoryUtilization,
];
const IMPACT_IDS: WorkloadsDashboardItemId[] = [
  WorkloadsDashboardItemId.CpuImpact,
  WorkloadsDashboardItemId.MemoryImpact,
];

// Items visible by default (before user saves their own layout) per category.
// Default view: curated Phase-1 set (not every WOOP/category card).
// Unimplemented Figma cards (Attention needed, Idle costs) are omitted —
// layout positions leave empty space for them.
export const WORKLOADS_DASHBOARD_DEFAULT_VISIBLE: Record<
  WorkloadCategory,
  WorkloadsDashboardItemId[]
> = {
  [WORKLOAD_CATEGORIES.DEFAULT]: [
    WorkloadsDashboardItemId.AutoscalerImpact,
    WorkloadsDashboardItemId.RecommendationStatus,
    WorkloadsDashboardItemId.ComputeCost,
    WorkloadsDashboardItemId.WorkloadAutoscalerCoverage,
    WorkloadsDashboardItemId.ResourceUtilization,
    WorkloadsDashboardItemId.NetworkCost,
    WorkloadsDashboardItemId.OomKills,
    WorkloadsDashboardItemId.ReliabilityMap,
    WorkloadsDashboardItemId.CpuUtilization,
    WorkloadsDashboardItemId.MemoryUtilization,
    WorkloadsDashboardItemId.ComputeSpend,
  ],
  [WORKLOAD_CATEGORIES.AUTOSCALING]: [
    WorkloadsDashboardItemId.WorkloadAutoscalerCoverage,
    WorkloadsDashboardItemId.RecommendationStatus,
    WorkloadsDashboardItemId.ScalingPolicies,
    WorkloadsDashboardItemId.ReclaimedResources,
    WorkloadsDashboardItemId.SavingsSummary,
    ...UTILIZATION_IDS,
    WorkloadsDashboardItemId.OomKills,
  ],
  [WORKLOAD_CATEGORIES.SAVINGS]: [
    WorkloadsDashboardItemId.AutoscalerImpact,
    WorkloadsDashboardItemId.ReclaimedResources,
    WorkloadsDashboardItemId.SavingsSummary,
    ...IMPACT_IDS,
  ],
  [WORKLOAD_CATEGORIES.COMPUTE]: [
    WorkloadsDashboardItemId.ComputeCost,
    WorkloadsDashboardItemId.BiggestCostMovers,
    WorkloadsDashboardItemId.ComputeSpend,
  ],
  [WORKLOAD_CATEGORIES.RELIABILITY]: [
    WorkloadsDashboardItemId.ReliabilityMap,
    WorkloadsDashboardItemId.WorkloadHealth,
    WorkloadsDashboardItemId.WorkloadHealthTrafficShare,
    WorkloadsDashboardItemId.P95LatencyDistribution,
  ],
  [WORKLOAD_CATEGORIES.NETWORK]: [
    WorkloadsDashboardItemId.NetworkCost,
    WorkloadsDashboardItemId.NetworkTraffic,
    WorkloadsDashboardItemId.NetworkCostOverTime,
  ],
  [WORKLOAD_CATEGORIES.GPU_UTILIZATION]: [
    WorkloadsDashboardItemId.GpuWorkloads,
    WorkloadsDashboardItemId.GpuMemoryWaste,
    WorkloadsDashboardItemId.GpuIdleSpend,
  ],
};

/** Primary category per card (folder ownership) for customize-drawer grouping/filter. */
export const WORKLOADS_DASHBOARD_ITEM_CATEGORY: Record<
  WorkloadsDashboardItemId,
  Exclude<WorkloadCategory, typeof WORKLOAD_CATEGORIES.DEFAULT>
> = {
  [WorkloadsDashboardItemId.CpuUtilization]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadsDashboardItemId.MemoryUtilization]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadsDashboardItemId.ResourceUtilization]:
    WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadsDashboardItemId.OomKills]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadsDashboardItemId.WorkloadAutoscalerCoverage]:
    WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadsDashboardItemId.RecommendationStatus]:
    WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadsDashboardItemId.ScalingPolicies]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadsDashboardItemId.CpuImpact]: WORKLOAD_CATEGORIES.SAVINGS,
  [WorkloadsDashboardItemId.MemoryImpact]: WORKLOAD_CATEGORIES.SAVINGS,
  [WorkloadsDashboardItemId.AutoscalerImpact]: WORKLOAD_CATEGORIES.SAVINGS,
  [WorkloadsDashboardItemId.ReclaimedResources]: WORKLOAD_CATEGORIES.SAVINGS,
  [WorkloadsDashboardItemId.SavingsSummary]: WORKLOAD_CATEGORIES.SAVINGS,
  [WorkloadsDashboardItemId.NetworkCost]: WORKLOAD_CATEGORIES.NETWORK,
  [WorkloadsDashboardItemId.NetworkTraffic]: WORKLOAD_CATEGORIES.NETWORK,
  [WorkloadsDashboardItemId.NetworkCostOverTime]: WORKLOAD_CATEGORIES.NETWORK,
  [WorkloadsDashboardItemId.GpuWorkloads]: WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadsDashboardItemId.GpuMemoryWaste]:
    WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadsDashboardItemId.GpuIdleSpend]: WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadsDashboardItemId.ReliabilityMap]: WORKLOAD_CATEGORIES.RELIABILITY,
  [WorkloadsDashboardItemId.WorkloadHealth]: WORKLOAD_CATEGORIES.RELIABILITY,
  [WorkloadsDashboardItemId.WorkloadHealthTrafficShare]:
    WORKLOAD_CATEGORIES.RELIABILITY,
  [WorkloadsDashboardItemId.P95LatencyDistribution]:
    WORKLOAD_CATEGORIES.RELIABILITY,
  [WorkloadsDashboardItemId.ComputeCost]: WORKLOAD_CATEGORIES.COMPUTE,
  [WorkloadsDashboardItemId.BiggestCostMovers]: WORKLOAD_CATEGORIES.COMPUTE,
  [WorkloadsDashboardItemId.ComputeSpend]: WORKLOAD_CATEGORIES.COMPUTE,
};

export const getWorkloadsDashboardStorageKey = (category: string) =>
  `workloads-dashboard-${category}-v10`;
